import { ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import {
  useBulkGenerateStudentLinkingCodesMutation,
  useExportStudentLinkingCodesLazyQuery,
  useGenerateStudentLinkingCodeMutation,
} from '@/types/vue-apollo-helpers';
import { linkingCodesCsv } from '@/utils/guardianAdmin';
import { getErrorMessage } from '@/utils/error';

/**
 * Parent-portal linking codes: generate one student's, generate for every
 * student without one, and download them all as CSV.
 */
export function useStudentLinkingCodes() {
  const toast = useToast();
  const { mutate: generateMutate } = useGenerateStudentLinkingCodeMutation({});
  const { mutate: bulkMutate, loading: bulkGenerating } = useBulkGenerateStudentLinkingCodesMutation({});
  const { load, refetch, onResult, onError } = useExportStudentLinkingCodesLazyQuery({ fetchPolicy: 'network-only' });
  const generatingFor = ref<string | null>(null);
  const exporting = ref(false);

  /** The new code, or null after telling the user why not. */
  async function generateFor(studentId: string): Promise<string | null> {
    generatingFor.value = studentId;
    try {
      const result = await generateMutate({ studentId });
      const code = result?.data?.generateStudentLinkingCode?.linking_code ?? null;
      if (!code) throw new Error('No code was returned.');
      toast.add({ severity: 'success', summary: 'Code generated', detail: code, life: 4000 });
      return code;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Not generated', detail: getErrorMessage(error, 'The linking code could not be generated.'), life: 5000 });
      return null;
    } finally {
      generatingFor.value = null;
    }
  }

  async function generateMissing(): Promise<void> {
    try {
      const result = await bulkMutate({ force: false });
      const data = result?.data?.bulkGenerateStudentLinkingCodes;
      toast.add(!data?.generated
        ? { severity: 'info', summary: 'Nothing to do', detail: 'Every student already has a linking code.', life: 4000 }
        : { severity: 'success', summary: 'Codes generated', detail: `${data.generated} generated, ${data.skipped} already had one.`, life: 5000 });
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Not generated', detail: getErrorMessage(error, 'Linking codes could not be generated.'), life: 5000 });
    }
  }

  onResult((result) => {
    if (result.loading) return;
    exporting.value = false;
    const rows = result.data?.exportStudentLinkingCodes ?? [];
    if (!rows.length) {
      toast.add({ severity: 'warn', summary: 'No codes yet', detail: 'No student has a linking code yet. Generate codes first.', life: 4000 });
      return;
    }
    const url = URL.createObjectURL(new Blob([linkingCodesCsv(rows)], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'student-linking-codes.csv';
    link.click();
    URL.revokeObjectURL(url);
    toast.add({ severity: 'success', summary: 'Downloaded', detail: `${rows.length} linking codes exported.`, life: 3000 });
  });

  onError((error) => {
    exporting.value = false;
    toast.add({ severity: 'error', summary: 'Not downloaded', detail: getErrorMessage(error, 'Linking codes could not be exported.'), life: 5000 });
  });

  function downloadCsv() {
    exporting.value = true;
    if (!load()) void refetch();
  }

  return { generateFor, generatingFor, generateMissing, bulkGenerating, downloadCsv, exporting };
}
